import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import './DataSets.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { useNavigate } from 'react-router-dom'
import {
  datasetsAdapter,
  getAllDatasets,
  removeDataset
} from '../../redux/slices'
import { DefaultState } from '../../types'
import { Container, Col, Row, ModalBody } from 'reactstrap'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch } from '../../services/lib'
import { ICSModal, TanStackDatagridWithExport } from '../../components'
import DataSetReview from './DataSetReview/DataSetReview'
import { DatasetTypeModal } from './Modals/DatasetTypeModal/DatasetTypeModal'
import {
  TanStackDatagridColumn,
  TanStackDatagridPagination,
  TanStackDatagridRowAction
} from '../../components/TanStackDataGrid/TanStackDatagrid'
import { DataSet } from './types'
import { useTimezone } from '../../hooks/useTimezone'

interface DataSetRow extends DataSet {
  created_at?: string
  description?: string
  user?: {
    name?: string
  }
}

const DataSetList = (): JSX.Element => {
  const navigate = useNavigate()
  const [fileModalVisible, setFileModalVisible] = useState<boolean>(false)
  const [selectedDS, setSelectedDS] = useState<string>('')
  const { formatLocalTimeFromUtc } = useTimezone()
  const datasetsState = useSelector((state: DefaultState) => state.datasets)
  const [pagination, setPagination] = useState<TanStackDatagridPagination>({
    pageIndex: 0,
    pageSize: 10
  })

  const [activeDatasetTypeModal, setActiveDatasetTypeModal] =
    useState<boolean>(false)
  const handleCloseModal = () => {
    setActiveDatasetTypeModal(false)
  }

  const datasetErrors = useSelector(
    (state: DefaultState) => state.datasets.error
  )
  const isAdmin: boolean | undefined = useSelector((state: DefaultState) =>
    state.session.data.user?.roles?.includes('admin')
  )
  const datasetsArray = datasetsAdapter
    .getSelectors()
    .selectAll(datasetsState) as DataSetRow[]

  const dispatch: AppDispatch = useDispatch()

  useEffect(() => {
    const initialState = async () => {
      await dispatch(getAllDatasets())
    }
    initialState()
  }, [dispatch])

  const datasetsAddButtonClicked = () => {
    if (isAdmin) {
      setActiveDatasetTypeModal(true)
    } else {
      navigate('/ui2/datasets/generate/')
    }
  }

  const data = useMemo(() => datasetsArray ?? [], [datasetsArray])

  const handleDelete = useCallback(
    async (dataset: DataSetRow) => {
      const r = window.confirm(
        `Are you sure you want to delete the following Dataset: ${dataset.name}?`
      )

      if (r === true) {
        await dispatch(removeDataset(String(dataset.id)))
        await dispatch(getAllDatasets())
      }
    },
    [dispatch]
  )

  const handleView = useCallback((dataset: DataSetRow) => {
    setSelectedDS(String(dataset.id))
    setFileModalVisible(true)
  }, [])

  const columns = useMemo<TanStackDatagridColumn<DataSetRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: (dataset) => dataset.name || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'type',
        header: 'Type',
        accessor: (dataset) => dataset.type || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true,
        width: 70
      },
      {
        id: 'description',
        header: 'Description',
        accessor: (dataset) => dataset.description || '',
        cell: (dataset) => (
          <ICSDataCellDetailView
            value={dataset.description || ''}
            data={dataset}
            row={{ data: dataset }}
            modalWidth="35vw"
            modalHeight="20vh"
          />
        ),
        className: 'td-dataset-description',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'userName',
        header: 'User',
        accessor: (dataset) => dataset.user?.name || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true,
        width: 180
      },
      {
        id: 'created_at',
        header: 'Create Date',
        accessor: (dataset) => dataset.created_at || '',
        cell: (dataset) => formatLocalTimeFromUtc(dataset.created_at) ?? '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true,
        width: 180
      }
    ],
    [formatLocalTimeFromUtc]
  )

  const actions = useMemo<TanStackDatagridRowAction<DataSetRow>[]>(() => {
    const nextActions: TanStackDatagridRowAction<DataSetRow>[] = [
      {
        id: 'view',
        icon: 'lucide#grid-2x2-x',
        label: 'View',
        hidden: (dataset) => dataset.type === 'dynamic',
        onClick: handleView
      }
    ]

    if (isAdmin) {
      nextActions.unshift({
        id: 'delete',
        icon: 'lucide#trash',
        label: 'Delete',
        onClick: handleDelete
      })
    }

    return nextActions
  }, [handleDelete, handleView, isAdmin])

  return (
    <>
      {activeDatasetTypeModal && (
        <DatasetTypeModal onClose={handleCloseModal} />
      )}
      <Container fluid={true}>
        <Row>
          <Col className="wivi-col">
            <HeaderBar
              origin="IPA"
              routes={[
                {
                  title: 'Data Sets'
                }
              ]}
              help={{ title: 'Data Sets', text: helpText }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className="wivi-col">
            <ComponentBox>
              <ICSModal
                visible={fileModalVisible}
                onHiding={() => {
                  setFileModalVisible(false)
                }}
                title="Dataset Files"
                defaultWidth="80vw"
                defaultHeight="50vh"
                resizeEnabled={true}
              >
                <ModalBody>
                  {fileModalVisible && <DataSetReview datasetId={selectedDS} />}
                </ModalBody>
              </ICSModal>
              {datasetErrors?.code === 'error' ? (
                <ICSErrorCard
                  code={datasetErrors.code}
                  message={datasetErrors.message}
                  fields={datasetErrors.fields}
                />
              ) : (
                <TanStackDatagridWithExport
                  data={data}
                  columns={columns}
                  actions={actions}
                  getRowId={(dataset) => String(dataset.id)}
                  enablePagination
                  pagination={pagination}
                  onPaginationChange={setPagination}
                  toolbarContent={
                    <ICSActionButton
                      id="datasetAddButton"
                      type="success"
                      width={65}
                      height={35}
                      stylingMode="contained"
                      onClick={datasetsAddButtonClicked}
                      text="Add"
                    />
                  }
                />
              )}
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}
export default DataSetList

const helpText = (
  <div>
    <p>Data Sets define the source data passed to your Analyzer.</p>
    <p>
      Data Sets can be generated either by selecting criteria, or by selecting
      specific files from the data query.
    </p>
    <p>
      <strong>Files</strong>
    </p>
    <p>
      Simply navigate to Generate page, perform a search, select your desired
      files and click "Create DataSet".
    </p>
    <p>
      This will generate a Static Data Set which can be used for on demand
      Analysis.
    </p>

    <p>
      <strong>Criteria</strong>
    </p>
    <p>
      A dynamic Data Set is a collection of criteria in any combination or
      number of:
    </p>
    <ul>
      <li>Fleets and/or Vehicles</li>
      <li>Collections and/or file names</li>
      <li>File extensions</li>
      <li>Dates</li>
      <li>Scripts</li>
    </ul>
    <p>
      These Data Sets must be either Static, which are used for on demand
      analysis on existing files, or Dynamic, which are used for ongoing
      analysis of files as they are collected
    </p>

    <p>
      You may assign multiple Data Sets to an Analyzer so there is no need to
      create or maintain a single all-inclusive Data Set.
    </p>
    <p>
      Data Sets created by you are only available to you so may add, edit, or
      discard them at any time.
    </p>
  </div>
)
